import { useState, useMemo } from 'react';
import DotPagination from './DotPagination';

interface TeamMember {
    employee_id: number;
    employee_name: string;
    shared_goals_count: number;
}

interface TeamMembersSectionProps {
    teamMembers: TeamMember[];
    loading: boolean;
    error: any;
}

const ITEMS_PER_PAGE = 5;

const TeamMembersSection = ({ teamMembers, loading, error }: TeamMembersSectionProps) => {
    const [currentPage, setCurrentPage] = useState(0);

    // Calculate pagination
    const totalPages = Math.ceil(teamMembers.length / ITEMS_PER_PAGE);
    const startIndex = currentPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentMembers = useMemo(() => teamMembers.slice(startIndex, endIndex), [teamMembers, startIndex, endIndex]);

    // Reset to page 1 when team members change
    useMemo(() => {
        setCurrentPage(0);
    }, [teamMembers.length]);
    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 leftsidebar-card-dv">
            <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center shadow-md">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">Team Members</h3>
                </div>
                
                {loading ? (
                    <div className="flex flex-col gap-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center gap-3 animate-pulse p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                <div className="w-10 h-10 rounded-full bg-surface-bg-secondary" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-surface-bg-secondary rounded w-3/4" />
                                    <div className="h-3 bg-surface-bg-secondary rounded w-1/2" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="text-center py-4 text-danger-active text-sm">
                        Error loading team members
                    </div>
                ) : teamMembers.length === 0 ? (
                    <div className="text-center py-8">
                        <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <p className="text-text-text-secondary text-sm">No team members found</p>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col gap-3 mb-4">
                            {currentMembers.map((member, index) => (
                                <div 
                                    key={member.employee_id} 
                                    className="group flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700/50 dark:to-blue-900/20 rounded-xl hover:shadow-md transition-all duration-300 border border-transparent hover:border-blue-200 dark:hover:border-blue-700 teammember-items"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-teal-400 rounded-full blur opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
                                        <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-teal-500 text-white flex items-center justify-center text-sm font-bold shadow-md group-hover:scale-110 transition-transform duration-300">
                                            {member.employee_name.charAt(0).toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm font-semibold text-gray-800 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                            {member.employee_name}
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>{member.shared_goals_count} {member.shared_goals_count === 1 ? 'shared goal' : 'shared goals'}</span>
                                        </div>
                                    </div>
                                    <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            ))}
                        </div>
                        {totalPages > 1 && (
                            <DotPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TeamMembersSection;
export type { TeamMember };
